import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Avatar,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import TranslateIcon from '@mui/icons-material/Translate';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import StarIcon from '@mui/icons-material/Star';
import { theme } from '../../../theme';
import ReviewService from '../../../services/review.service';
import TranslationValidationModal from '../../pro/salon/TranslationValidationModal';

const buildTranslationsState = (translations = {}) => ({
    fr: translations?.fr || '',
    en: translations?.en || '',
    ar: translations?.ar || '',
});

const sanitizeTranslations = (translations = {}) => (
    Object.fromEntries(
        Object.entries(translations)
            .map(([language, value]) => [language, (value || '').trim()])
            .filter(([, value]) => value)
    )
);

const SalonHero = ({ salon, onUpdate }) => {
    const [editOpen, setEditOpen] = useState(false);
    const [translationOpen, setTranslationOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});

    const [reviewsSummary, setReviewsSummary] = useState({ average: null, count: 0 });

    const primaryPhoto = salon.primary_photo || salon.photos?.find(p => p.is_primary)?.url || salon.photos?.[0]?.url;

    useEffect(() => {
        const loadReviewsSummary = async () => {
            if (!salon?.salon_id) return;
            try {
                const data = await ReviewService.getSalonReviews(salon.salon_id);
                const items = Array.isArray(data) ? data : data?.results || [];
                const visible = items.filter((r) => !r?.is_hidden);
                const count = visible.length;
                const average = count
                    ? visible.reduce((sum, r) => sum + (Number(r?.rating) || 0), 0) / count
                    : null;
                setReviewsSummary({ average, count });
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error('Failed to load salon reviews summary:', e);
                setReviewsSummary({ average: null, count: 0 });
            }
        };

        loadReviewsSummary();
    }, [salon?.salon_id]);

    const handleEditClick = () => {
        setFormData({
            name: salon.name || '',
            description: salon.description || '',
            name_translations: buildTranslationsState(salon.name_translations),
            description_translations: buildTranslationsState(salon.description_translations),
            phone: salon.phone || '',
            email: salon.email || '',
        });
        setEditOpen(true);
    };

    const calculateRating = () => {
        // TODO: Implement real rating calculation when available in backend
        return typeof reviewsSummary.average === 'number' ? reviewsSummary.average.toFixed(1) : '—';
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            await onUpdate(salon.salon_id, {
                ...formData,
                name_translations: sanitizeTranslations(formData.name_translations),
                description_translations: sanitizeTranslations(formData.description_translations),
                translations_auto: false,
            });
            setEditOpen(false);
        } catch (error) {
            console.error("Failed to update salon", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenTranslations = () => {
        setTranslationOpen(true);
    };

    const handleSaveTranslations = async (editedTranslations) => {
        try {
            setLoading(true);
            console.log('[DEBUG] Frontend sending translations:', {
                name_translations: sanitizeTranslations(editedTranslations.name),
                description_translations: sanitizeTranslations(editedTranslations.description),
                address_translations: sanitizeTranslations(editedTranslations.address),
            });
            
            const updateData = {
                name_translations: sanitizeTranslations(editedTranslations.name),
                description_translations: sanitizeTranslations(editedTranslations.description),
                address_translations: sanitizeTranslations(editedTranslations.address),
                translations_auto: false,
            };
            
            await onUpdate(salon.salon_id, updateData);
            
            // Update local state immediately to reflect changes
            if (salon) {
                Object.assign(salon, {
                    name_translations: updateData.name_translations,
                    description_translations: updateData.description_translations,
                    address_translations: updateData.address_translations,
                    translations_auto: updateData.translations_auto,
                });
                console.log('[DEBUG] Local salon state updated:', {
                    name_translations: salon.name_translations,
                    description_translations: salon.description_translations,
                    address_translations: salon.address_translations,
                });
            }
            
            setTranslationOpen(false);
        } catch (error) {
            console.error('Failed to update salon translations', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log('[DEBUG] Salon data updated:', {
            formatted_address: salon.formatted_address,
            address_translations: salon.address_translations,
        });
    }, [salon.formatted_address, salon.address_translations]);

    return (
        <Box sx={{ position: 'relative', mb: 3 }}>
            {/* Cover Image Area */}
            <Box
                sx={{
                    height: { xs: 200, md: 300 },
                    width: '100%',
                    backgroundImage: primaryPhoto ? `url(${primaryPhoto})` : `linear-gradient(45deg, ${theme.colors.primary[800]}, ${theme.colors.primary[500]})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: { xs: 0, md: theme.borderRadius.xl },
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: theme.boxShadow.lg
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 60%)'
                    }}
                />

                {/* Quick Actions overlay */}
                <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={<TranslateIcon />}
                            onClick={handleOpenTranslations}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                color: '#fff',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                            }}
                        >
                            Vérifier traduction
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={handleEditClick}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                color: '#fff',
                                fontWeight: 700,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                            }}
                        >
                            Modifier
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Info Bar */}
            <Box sx={{ px: { xs: 2, md: 4 }, mt: { xs: -6, md: -4 }, position: 'relative', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-end' }, gap: { xs: 2, md: 3 } }}>
                {/* Avatar / Logo */}
                <Avatar
                    src={primaryPhoto}
                    alt={salon.name}
                    sx={{
                        width: { xs: 100, md: 120 },
                        height: { xs: 100, md: 120 },
                        border: `4px solid #fff`,
                        boxShadow: theme.boxShadow.md,
                        bgcolor: theme.colors.background.secondary,
                        fontSize: { xs: '2.5rem', md: '3rem' },
                        fontWeight: 800,
                        color: theme.colors.primary[700]
                    }}
                >
                    {salon.name?.charAt(0)}
                </Avatar>

                {/* Text Content */}
                <Box sx={{ flex: 1, pb: 1, textAlign: { xs: 'center', md: 'left' } }}>
                    <Typography variant="h3" sx={{ fontWeight: 900, color: theme.colors.text.primary, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }, mb: 1 }}>
                        {salon.name}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2 }, alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, color: theme.colors.text.secondary }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ color: theme.colors.warning[500], fontSize: 20 }} />
                            <Typography sx={{ fontWeight: 700, color: theme.colors.text.primary }}>{calculateRating()}</Typography>
                            <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>({reviewsSummary.count} avis)</Typography>
                        </Box>

                        {salon.formatted_address && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon sx={{ fontSize: 18, color: theme.colors.primary[500] }} />
                                <Typography variant="body2">{salon.formatted_address}</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Contact Chips */}
                <Box sx={{ display: 'flex', gap: 1, pb: { xs: 0.5, md: 1.5 }, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    {salon.phone && (
                        <Chip
                            icon={<PhoneIcon sx={{ fontSize: '1rem !important' }} />}
                            label={salon.phone}
                            onClick={() => { }}
                            sx={{ bgcolor: theme.colors.background.secondary, fontWeight: 600 }}
                        />
                    )}
                    {salon.email && (
                        <Chip
                            icon={<EmailIcon sx={{ fontSize: '1rem !important' }} />}
                            label={salon.email}
                            onClick={() => { }}
                            sx={{ bgcolor: theme.colors.background.secondary, fontWeight: 600 }}
                        />
                    )}
                </Box>
            </Box>

            {/* Description */}
            {salon.description && (
                <Box sx={{ mt: 3, px: { xs: 2, md: 4 } }}>
                    <Typography variant="body1" sx={{ color: theme.colors.text.secondary, maxWidth: 800, lineHeight: 1.6 }}>
                        {salon.description}
                    </Typography>
                </Box>
            )}

            {/* Edit Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800, borderBottom: `1px solid ${theme.colors.border.light}` }}>Modifier le salon</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Nom du salon"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Téléphone"
                                value={formData.phone}
                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                fullWidth
                            />
                            <TextField
                                label="Email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                                Traductions du nom
                            </Typography>
                            <TextField
                                label="Nom (FR)"
                                value={formData.name_translations?.fr || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    name_translations: {
                                        ...formData.name_translations,
                                        fr: e.target.value,
                                    }
                                })}
                                fullWidth
                            />
                            <TextField
                                label="Nom (EN)"
                                value={formData.name_translations?.en || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    name_translations: {
                                        ...formData.name_translations,
                                        en: e.target.value,
                                    }
                                })}
                                fullWidth
                            />
                            <TextField
                                label="Nom (AR)"
                                value={formData.name_translations?.ar || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    name_translations: {
                                        ...formData.name_translations,
                                        ar: e.target.value,
                                    }
                                })}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                                Traductions de la description
                            </Typography>
                            <TextField
                                label="Description (FR)"
                                value={formData.description_translations?.fr || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    description_translations: {
                                        ...formData.description_translations,
                                        fr: e.target.value,
                                    }
                                })}
                                multiline
                                rows={2}
                                fullWidth
                            />
                            <TextField
                                label="Description (EN)"
                                value={formData.description_translations?.en || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    description_translations: {
                                        ...formData.description_translations,
                                        en: e.target.value,
                                    }
                                })}
                                multiline
                                rows={2}
                                fullWidth
                            />
                            <TextField
                                label="Description (AR)"
                                value={formData.description_translations?.ar || ''}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    description_translations: {
                                        ...formData.description_translations,
                                        ar: e.target.value,
                                    }
                                })}
                                multiline
                                rows={2}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.colors.border.light}` }}>
                    <Button onClick={() => setEditOpen(false)} sx={{ fontWeight: 700, color: theme.colors.text.secondary }}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{ fontWeight: 800, boxShadow: theme.boxShadow.md }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
            <TranslationValidationModal
                open={translationOpen}
                onClose={() => setTranslationOpen(false)}
                onValidate={handleSaveTranslations}
                salonName={salon.name || ''}
                salonDescription={salon.description || ''}
                salonAddress={salon.formatted_address || ''}
                isLoading={false}
                translations={{
                    name: salon.name_translations || {},
                    description: salon.description_translations || {},
                    address: salon.address_translations || {},
                }}
            />
        </Box>
    );
};

export default SalonHero;
